// Pure view-value mirror for PrescriptionDetailsPage. No Vue, no axios. This
// mirror is DELIBERATELY ASYMMETRIC — hydrate maps 11 fields,
// apply maps 10 — and must stay that way: symmetrizing it would reintroduce the
// wiped-clinical-values regression this module exists to kill. dispensedQuantity
// is HYDRATE-ONLY; it is never written back through apply.
//
// The two side effects a pure function cannot own — normalizing prescription
// .fillDate to a Date, and toggling the settingViewValues guard — stay in thin
// page wrappers (setViewValues / assignViewValues).

import moment from 'moment'
import { RefillStatusCode } from '@/models/Prescription'

/** The prescription fields hydrate reads. */
export interface HydratablePrescription {
    dawCode: number
    originCode: number
    status: number
    subStatus: number | null | undefined
    nextTouchDate: string | Date | null | undefined
    nextTouchDateManualOverride: boolean | null | undefined
    deliveryCode: number
    dispensed: number
    expirationDate: string | Date | null | undefined
    writtenDate: string | Date | null | undefined
    treatmentTypeCode: number | null | undefined
}

/** The 11 view-model fields. dispensedQuantity is hydrate-only. */
export interface RxViewValues {
    dawCode: number
    originCode: number
    statusCode: number
    substatusCode: number | null
    nextTouchDateLocal: string
    nextTouchDateManualOverride: boolean
    deliveryCode: number
    dispensedQuantity: number
    expiration: Date | null
    writtenDate: Date
    treatmentTypeCode: number | null
}

/** The prescription fields apply writes. Note: NO `dispensed`. */
export interface AppliedPrescriptionFields {
    dawCode: number
    originCode: number
    status: number
    subStatus: number | null
    deliveryCode: number
    treatmentTypeCode: number | null
    nextTouchDate: Date | null
    nextTouchDateManualOverride: boolean
    expirationDate: Date | null
    writtenDate: Date
    /** Present only when the origin pharmacy has loaded (see applyViewValues). */
    originPharmacyId?: number
}

/**
 * Map a prescription onto the 11 view-model fields. Pure — the caller normalizes
 * prescription.fillDate and manages the settingViewValues guard.
 */
export function hydrateViewValues(p: HydratablePrescription): RxViewValues {
    const utcWritten = moment.utc(p.writtenDate)
    return {
        dawCode: p.dawCode,
        originCode: p.originCode,
        statusCode: p.status,
        substatusCode: p.subStatus ?? null,
        nextTouchDateLocal: p.nextTouchDate
            ? moment.utc(p.nextTouchDate).local().format('YYYY-MM-DDTHH:mm')
            : '',
        nextTouchDateManualOverride: p.nextTouchDateManualOverride ?? false,
        deliveryCode: p.deliveryCode,
        // HYDRATE-ONLY: dispensedQuantity comes from prescription.dispensed but is
        // never written back through applyViewValues.
        dispensedQuantity: p.dispensed,
        expiration: p.expirationDate ? moment(p.expirationDate).toDate() : null,
        writtenDate: new Date(utcWritten.year(), utcWritten.month(), utcWritten.date()),
        treatmentTypeCode: p.treatmentTypeCode ?? null,
    }
}

/**
 * Map the view-model back onto the 10 prescription fields it owns. Pure — returns
 * the fields to assign; the caller merges them onto the entity.
 *
 * Asymmetries preserved verbatim:
 * - dispensedQuantity is NOT written (that sync lives only in changeDispense, the
 *   MedSync short-fill block, and the save-time default).
 * - subStatus is kept only for OnHold/Discontinued, nulled otherwise.
 * - originPharmacyId is copied ONLY when loaded, so an early save during the async
 *   originPharmacy load window can't blank a valid persisted id.
 */
export function applyViewValues(
    view: RxViewValues,
    opts: { originPharmacyId: number | null | undefined },
): AppliedPrescriptionFields {
    const applied: AppliedPrescriptionFields = {
        dawCode: view.dawCode,
        originCode: view.originCode,
        status: view.statusCode,
        subStatus:
            view.statusCode == RefillStatusCode.OnHold ? view.substatusCode :
                view.statusCode == RefillStatusCode.Discontinued ? view.substatusCode :
                    null,
        deliveryCode: view.deliveryCode,
        treatmentTypeCode: view.treatmentTypeCode,
        nextTouchDate: view.nextTouchDateLocal
            ? moment(view.nextTouchDateLocal).utc().toDate()
            : null,
        nextTouchDateManualOverride: view.nextTouchDateManualOverride,
        expirationDate: view.expiration,
        writtenDate: moment(view.writtenDate).startOf('day').toDate(),
    }
    if (opts.originPharmacyId) {
        applied.originPharmacyId = opts.originPharmacyId
    }
    return applied
}
